/**
 * Extended item-card flavor for collectibles: a 2-3 sentence backstory and a
 * comedic (non-binding) "effect" line, keyed by catalog slug. The short
 * one-liner already lives in the DB `description`; this adds the deeper lore a
 * game inventory card would show. Shop-custom items have no entry here — the
 * card falls back to their DB description + a generic effect.
 */

export interface CollectibleLore {
  backstory: string;
  effect: string;
}

export const COLLECTIBLE_LORE: Record<string, CollectibleLore> = {
  chipped_mug: {
    backstory:
      'Pulled from the third dish rack of an arcade café that closed in the last century. Every chip is a coffee it survived. It has strong opinions about decaf and is not shy about them.',
    effect: 'EFFECT: +3% morning optimism (not legally binding)',
  },
  save_point_coaster: {
    backstory:
      'Set your cup down here and the moment is preserved forever. Baristas whisper that if you rest a drink on it at exactly the right second, the day cannot get worse. Results unverified.',
    effect: 'EFFECT: auto-saves your progress every sip',
  },
  last_biscotti: {
    backstory:
      'There is always exactly one left in the jar. Nobody has ever taken it and nobody has ever refilled it. Physicists gave up. The biscotti abides.',
    effect: 'EFFECT: infinite snack, finite courage to eat it',
  },
  pixel_crema: {
    backstory:
      'Perfect foam rendered at eight bits per bubble. Under a loupe you can count the pixels; each one is a tiny reflection of a happier timeline where you slept in.',
    effect: 'EFFECT: +5 charm, -1 depth perception',
  },
  overclocked_kettle: {
    backstory:
      'Boils water a half-second before you decide you want it. It has been reported to the laws of thermodynamics twice. Both cases were dismissed for lack of a working clock.',
    effect: 'EFFECT: brews slightly before you ask',
  },
  stirrer_404: {
    backstory:
      'Stir Not Found. The coffee remains eternally unstirred, sugar suspended mid-swirl. Some baristas consider this a bug. The regulars consider it a feature.',
    effect: 'EFFECT: 404 — smoothness not located',
  },
  eternal_keepcup: {
    backstory:
      'Has outlived three laptops, one lease, and a minor civilisation. The lid still seals perfectly. It will attend your funeral, faintly smelling of oat milk.',
    effect: 'EFFECT: cannot be lost, only misplaced forever',
  },
  cracked_tamper: {
    backstory:
      'Tamps espresso and expectations with equal, unbothered force. The crack down its side happened on a Monday that everyone agreed never to speak of again.',
    effect: 'EFFECT: +8 brew power on Mondays only',
  },
  glitch_latte: {
    backstory:
      'The latte art shifted mid-pour into something that was definitely not a fern. It watches you now. It knows how you take your coffee. It approves, mostly.',
    effect: 'EFFECT: the art is looking at you (it likes you)',
  },
  espresso_cartridge: {
    backstory:
      'When the shot will not pull, you blow on the cartridge and blow into the group head, exactly like the old machines. It should not work. It always works.',
    effect: 'EFFECT: blow on it to fix (60% of the time, works every time)',
  },
  level99_grinder: {
    backstory:
      'Maxed out. Grinds bean, time, and lingering doubt into a fine, even powder. The dial goes to 99 and then to a small drawing of a skull. Nobody has used the skull setting twice.',
    effect: 'EFFECT: grinds beans, deadlines, and self-doubt',
  },
  golden_portafilter: {
    backstory:
      'Awarded to baristas who never once missed a dose across a thousand shots. It is heavier than gold has any right to be, weighted with the confidence of a perfect pour.',
    effect: 'EFFECT: +12 charm, dose is always exactly right',
  },
  mystery_13th_cup: {
    backstory:
      'Your loyalty card only had ten slots. Then twelve. This is the thirteenth cup and no one on the staff will explain where it came from or why it is warm.',
    effect: 'EFFECT: exists between stamps 12 and 1',
  },
  diamond_stirrer: {
    backstory:
      'For stirring matters of genuine consequence. Cuts through crema, cream, and small talk. Rumoured to have settled at least one board meeting and one divorce.',
    effect: 'EFFECT: dissolves sugar and awkward silences',
  },
  founders_bean: {
    backstory:
      'One of the ten original beans from the first roast, back when the whole operation fit on a single counter. It hums, very quietly, a tune nobody can quite place.',
    effect: 'EFFECT: hums the founding melody (+15 luck)',
  },
  konami_roast: {
    backstory:
      'Thirty extra lives per cup, legally dubious in four jurisdictions. Brewed only by those who remember the code. Tastes faintly of an arcade carpet and pure, uncut nostalgia.',
    effect: 'EFFECT: ↑↑↓↓←→←→BA — 30 extra mornings',
  },
};

// Fallback for shop-custom items with no lore entry.
export function loreFor(slug: string, description: string): CollectibleLore {
  return (
    COLLECTIBLE_LORE[slug] || {
      backstory: description || 'A one-of-a-kind item from a shop that knows exactly what it is doing.',
      effect: 'EFFECT: locally sourced, mysteriously potent',
    }
  );
}
